import { useState, useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { PageHead } from "@/components/PageHead";
import { img, COLLECTIONS, slugify } from "@/lib/site";

const BannerSlider = ({ slides, fit = "cover" }) => {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setIndex((i) => (i + 1) % slides.length), 5000);
    return () => clearInterval(id);
  }, [slides.length]);
  const objectClass = fit === "contain" ? "object-contain" : "object-cover";
  const objectStyle = fit === "bottom" ? { objectPosition: "center bottom" } : undefined;
  return (
    <div className={`group relative aspect-[16/9] w-full overflow-hidden ${fit === "contain" ? "bg-secondary/40" : "bg-black"}`} data-testid="collection-banner-slider">
      {slides.map((src, i) => (
        <motion.img
          key={src}
          src={src}
          alt={`Slide ${i + 1}`}
          data-testid={`collection-banner-slide-${i}`}
          className={`absolute inset-0 h-full w-full ${objectClass} transition-transform duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105`}
          style={objectStyle}
          initial={false}
          animate={{ opacity: i === index ? 1 : 0 }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
        />
      ))}
      <div className="absolute inset-x-0 bottom-0 z-10 flex items-center gap-3 px-5 pb-5" data-testid="collection-banner-dots">
        {slides.map((src, i) => (
          <button
            key={src}
            type="button"
            onClick={() => setIndex(i)}
            data-testid={`collection-banner-dot-${i}`}
            aria-label={`Show image ${i + 1}`}
            className={`h-[3px] transition-all duration-500 ${
              i === index ? "w-10 bg-white" : "w-5 bg-white/40 hover:bg-white/70"
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export default function CollectionPage() {
  const { slug } = useParams();
  const data = COLLECTIONS[slug];
  const [active, setActive] = useState("All");
  const [showFilter, setShowFilter] = useState(false);

  if (!data) return <Navigate to="/collection" replace />;

  if (data.comingSoonPage) {
    return (
      <PageHead
        eyebrow={`Our Collection / ${data.index}`}
        titleLines={["Coming", "soon."]}
      />
    );
  }

  const filters = ["All", ...data.subcategories];
  const products =
    active === "All" ? data.products : data.products.filter((p) => p.sub === active);

  return (
    <>
      <PageHead
        eyebrow={data.headEyebrow || `Our Collection / ${data.index}`}
        titleLines={data.headTitle || [data.name]}
        intro={data.intro}
      />

      <section className="edge pb-14 md:pb-20" data-testid="collection-banner">
        <div className="overflow-hidden">
          {data.heroSlides ? (
            <BannerSlider slides={data.heroSlides} fit={data.heroFit} />
          ) : (
            <motion.img
              initial={{ scale: 1.06 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
              src={img(data.banner, 2400)}
              alt={data.name}
              className="aspect-[16/9] w-full object-cover"
            />
          )}
        </div>
      </section>

      {data.brochure && (
        <section className="edge pb-14 md:pb-20" data-testid="collection-brochure">
          <a
            href={data.brochure.url}
            target="_blank"
            rel="noreferrer"
            download
            data-testid="collection-brochure-download"
            className="inline-flex items-center gap-3 border border-foreground px-6 py-3 text-xs uppercase tracking-[0.18em] transition-colors hover:bg-foreground hover:text-background"
          >
            Download {data.brochure.name} ↓
          </a>
        </section>
      )}

      {/* Product grid + filter */}
      <section className="edge hairline pb-24 md:pb-32" data-testid="collection-products">
        <div className="flex items-center justify-between py-6">
          <span className="eyebrow">{products.length} Products</span>
          <button
            onClick={() => setShowFilter((v) => !v)}
            data-testid="filter-toggle"
            className={`flex items-center gap-3 border hairline px-4 py-2.5 text-[0.7rem] uppercase tracking-[0.18em] transition-colors ${
              showFilter ? "bg-foreground text-background" : ""
            }`}
          >
            <span className="flex flex-col gap-[3px]">
              <span className="h-px w-4 bg-current" />
              <span className="h-px w-4 bg-current" />
              <span className="h-px w-4 bg-current" />
            </span>
            Filter
          </button>
        </div>

        {showFilter && (
          <div className="flex flex-wrap gap-3 hairline py-6" data-testid="filter-bar">
            {filters.map((f) => (
              <button
                key={f}
                onClick={() => setActive(f)}
                data-testid={`filter-${slugify(f)}`}
                className={`border hairline px-4 py-2 text-[0.7rem] uppercase tracking-[0.16em] transition-colors ${
                  active === f ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        )}

        <div className="grid grid-cols-2 gap-x-6 gap-y-12 hairline pt-10 md:grid-cols-3 lg:grid-cols-4">
          {products.map((p) => {
            const inner = (
              <>
                <div className={`${data.slug === "natural-stone" || data.squareGrid ? "aspect-square" : "aspect-[3/4]"} overflow-hidden bg-secondary/40`}>
                  <img
                    src={img(p.image, 900)}
                    alt={p.name}
                    className="h-full w-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105"
                  />
                </div>
                <div className="mt-4 flex items-start justify-between gap-2">
                  <h3 className="text-sm font-normal leading-snug tracking-editorial md:text-base">
                    {p.name}
                  </h3>
                  {p.comingSoon && (
                    <span className="shrink-0 border hairline px-2 py-0.5 text-[0.55rem] uppercase tracking-[0.16em] text-muted-foreground">
                      Soon
                    </span>
                  )}
                </div>
                {p.sub && <span className="mt-1 block eyebrow">{p.sub}</span>}
              </>
            );

            return p.comingSoon ? (
              <div
                key={p.slug}
                data-testid={`product-card-${p.slug}`}
                aria-disabled="true"
                className="block cursor-default opacity-55"
              >
                {inner}
              </div>
            ) : (
              <Link
                key={p.slug}
                to={`/collections/${slug}/${p.slug}`}
                data-testid={`product-card-${p.slug}`}
                className="group block"
              >
                {inner}
              </Link>
            );
          })}
        </div>
      </section>

      {data.chooseSlab && (
        <section className="edge hairline py-20 md:py-28" data-testid="collection-choose-slab">
          <div className="grid grid-cols-1 gap-10 md:grid-cols-12">
            <div className="md:col-span-3">
              <span className="eyebrow">{data.chooseSlab.eyebrow}</span>
            </div>
            <div className="md:col-span-8 md:col-start-4">
              <p className="text-xl font-light leading-relaxed tracking-editorial md:text-2xl">
                {data.chooseSlab.title}
              </p>
              {data.chooseSlab.paragraphs.map((para) => (
                <p key={para} className="mt-6 text-base leading-relaxed text-muted-foreground">
                  {para}
                </p>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="edge hairline py-24 text-center md:py-32" data-testid="collection-enquire">
        <p className="mx-auto max-w-xl text-2xl font-light tracking-editorial md:text-3xl">
          {data.slug === "natural-stone" ? "All can be made to order, custom." : "Request samples or get in touch."}
        </p>
        <Link
          to="/contact"
          data-testid="collection-enquire-cta"
          className="mt-8 inline-block border-b border-foreground pb-1 text-sm uppercase tracking-[0.16em]"
        >
          Enquire about {data.name} →
        </Link>
      </section>
    </>
  );
}
